export interface DashboardSearchItem {
  id: string;
  title: string;
  /** Short label shown below the title in the suggestion dropdown (e.g. the route). */
  routeLabel: string;
  keywords: string[];
  componentKey: string;
  /** ID of parent item when this is a child/nested section. */
  parent?: string;
  route: string;
  roles: string[];
}

export const DASHBOARD_SEARCH_ITEMS: DashboardSearchItem[] = [
  {
    id: "dash",
    title: "Dashboard",
    routeLabel: "Home → Dashboard",
    keywords: ["dashboard"],
    componentKey: "dashboard",
    route: "/dashboard",
    roles: ["admin", "approver", "sales", "ba", "marketing"],
  },
  {
    id: "kpi",
    title: "Status KPIs",
    routeLabel: "Dashboard → Status KPIs",
    keywords: [
      "kpi",
      "status",
      "cards",
      "approval",
      "quotes",
      "total",
      "raised",
      "rfp",
    ],
    componentKey: "kpi",
    route: "/dashboard",
    roles: ["admin", "approver", "sales", "ba", "marketing"],
  },
  {
    id: "ai-insights",
    title: "AI Insights",
    routeLabel: "Dashboard → AI Insights",
    keywords: ["ai", "insight", "gemini", "intelligence", "recommendations"],
    componentKey: "aiInsights",
    route: "/dashboard",
    roles: ["admin", "marketing"],
  },
  {
    id: "time-status",
    title: "Time Status",
    routeLabel: "Dashboard → Time Status",
    keywords: [
      "time",
      "ct",
      "completion time",
      "duration",
      "status time",
      "pending time",
      "proposal submission time status",
      "proposal submission",
      "time taken by ba",
      "c and t time",
      "completion time",
      "time taken to deliver c&t per card",
      "delay",
    ],
    componentKey: "timeStatus",
    route: "/dashboard",
    roles: ["admin", "marketing"],
  },
  {
    id: "analytics",
    title: "Analytics Overview",
    routeLabel: "Dashboard → Analytics Overview",
    keywords: [
      "analytics",
      "overview",
      "rfp",
      "graph",
      "activity",
      "recent",
      "chart",
      "revenue",
      "recent",
      "activity",
      "recent activity",
      "cost metrics",
      "cost",
    ],
    componentKey: "analytics",
    route: "/dashboard",
    roles: ["admin", "marketing"],
  },
  {
    id: "team-performance",
    title: "Team Performance",
    routeLabel: "Dashboard → Team Performance",
    keywords: [
      "team",
      "sales",
      "ba",
      "performance",
      "agent",
      "leaderboard",
      "ba performance",
      "sales performance",
      "team performance",
    ],
    componentKey: "teamPerf",
    route: "/dashboard",
    roles: ["admin", "marketing"],
  },
  {
    id: "sales-conversion",
    title: "Sales Conversion",
    routeLabel: "Dashboard → Team Performance → Sales Conversion",
    keywords: ["sales conversion", "conversion rate", "convert", "sales"],
    componentKey: "salesConversion",
    parent: "team-performance",
    route: "/dashboard",
    roles: ["admin"],
  },
  {
    id: "completion",
    title: "Proposal Completion",
    routeLabel: "Dashboard → Proposal Completion",
    keywords: [
      "completion",
      "proposal",
      "trend",
      "complete",
      "breakdown",
      "status breakdown",
      "Overall Completion",
      "completion trend",
      "proposal completion",
      "completion breakdown",
    ],
    componentKey: "completion",
    route: "/dashboard",
    roles: ["admin", "marketing"],
  },
  {
    id: "sales-wise-performance",
    title: "Sales-wise Performance",
    routeLabel: "Performance Analytics → Sales-wise Performance",
    keywords: [
      "sales-wise performance",
      "saleswise performance",
      "sales performance",
      "sales-wise",
      "saleswise",
    ],
    componentKey: "salesWisePerformance",
    route: "/performance-analytics",
    roles: ["admin"],
  },
  {
    id: "analytics",
    title: "RFP & Quotes Metrics",
    routeLabel: "Performance Analytics → RFP & Quotes Metrics",
    keywords: [
      "analytics",
      "sales-wise performance",
      "saleswise performance",
      "sales performance",
      "sales-wise",
      "saleswise",
      "cost metrics",
      "cost",
      "completion",
      "proposal completion",
      "completion breakdown",
      "rfp overview",
      "rfp graph",
      "quote graph",
      "rfp and quote metrics",
    ],
    componentKey: "analytics",
    route: "/performance-analytics",
    roles: ["admin"],
  },
  {
    id: "audit-logs",
    title: "Audit Logs",
    routeLabel: "Audit Logs → Audit Logs",
    keywords: ["audit logs", "audit", "logs"],
    componentKey: "auditLogs",
    route: "/audit-logs",
    roles: ["admin"],
  },
  {
    id: "user-management",
    title: "User Management",
    routeLabel: "User Management → User Management",
    keywords: ["user management", "user", "management", "users"],
    componentKey: "userManagement",
    route: "/users",
    roles: ["admin"],
  },
  {
    id: "settings",
    title: "Settings",
    routeLabel: "Settings → Settings",
    keywords: ["settings", "setting", "config", "configuration"],
    componentKey: "settings",
    route: "/settings",
    roles: ["admin"],
  },
  {
    id: "profile",
    title: "Profile",
    routeLabel: "Profile → Profile",
    keywords: ["profile", "user profile", "account", "change password"],
    componentKey: "profile",
    route: "/profile",
    roles: ["admin", "approver", "ba", "sales", "marketing"],
  },
  {
    id: "resources",
    title: "Resources",
    routeLabel: "Resources → Resources",
    keywords: ["resources", "resource", "help", "support", "resource library"],
    componentKey: "resources",
    route: "/resource-library",
    roles: ["admin"],
  },
  {
    id: "quotes",
    title: "Quotes",
    routeLabel: "Quotes → Quotes",
    keywords: ["quotes", "quote", "proposal"],
    componentKey: "quotes",
    route: "/quotes",
    roles: ["admin", "approver", "ba", "sales", "marketing"],
  },
  {
    id: "approval-activity",
    title: "Approval Activity",
    routeLabel: "Dashboard → Approval Activity",
    keywords: ["approval activity", "approval", "activity"],
    componentKey: "approvalActivity",
    route: "/dashboard",
    roles: ["approver"],
  },
  {
    id: "all-projects",
    title: "All Projects",
    routeLabel: "Dashboard → All Projects",
    keywords: ["all projects", "projects", "overview"],
    componentKey: "allProjects",
    route: "/dashboard",
    roles: ["approver", "ba"],
  },
  {
    id: "ba-wise-performance",
    title: "BA-wise Performance",
    routeLabel: "Dashboard → BA-wise Performance",
    keywords: ["ba-wise performance", "bawise performance"],
    componentKey: "baWisePerformance",
    route: "/dashboard",
    roles: ["approver"],
  },
  {
    id: "sales-wise-performance",
    title: "Sales-wise Performance",
    routeLabel: "Dashboard → Sales-wise Performance",
    keywords: [
      "sales-wise performance",
      "saleswise performance",
      "sales performance",
      "sales-wise",
      "saleswise",
    ],
    componentKey: "salesWisePerformance",
    route: "/dashboard",
    roles: ["sales", "approver", "ba", "marketing"],
  },
  {
    id: "my sales performance",
    title: "My Sales Performance",
    routeLabel: "Dashboard → My Sales Performance",
    keywords: [
      "sales-wise performance",
      "saleswise performance",
      "sales performance",
      "sales-wise",
      "saleswise",
    ],
    componentKey: "mySalesPerformance",
    route: "/dashboard",
    roles: ["sales", "approver"],
  },
  {
    id: "workload",
    title: "Workload & Costs",
    routeLabel: "Dashboard → Workload & Costs",
    keywords: ["workload", "workload and costs", "costs", "cost"],
    componentKey: "workloads",
    route: "/dashboard",
    roles: ["ba"],
  },
];
